import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { MobileMenuSheet } from "@/components/common";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import icMenu from "@/assets/icons/ic-menu.svg";
import icMessagePlusSquare from "@/assets/icons/ic-message-plus-square.svg";
import icArchive from "@/assets/icons/ic-archive.svg";
import icCpuChip from "@/assets/icons/ic-cpu-chip.svg";
import icRocket from "@/assets/icons/ic-rocket.svg";
import icUserCircle from "@/assets/icons/ic-user-circle.svg";
import LogoMobile from "@/assets/images/mobile-logo.svg";

export const Header = () => {
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-5 h-[64px] lg:px-8 lg:h-[88px]">
        <Link to="/" className="flex items-center">
          <img src={LogoMobile} alt="PwC AI Agent Marketplace" className="lg:hidden w-3/4" />
          <img
            src="/images/logos/logo-pwc-pc.svg"
            alt="PwC AI Agent Marketplace"
            className="hidden lg:block w-3/4"
          />
        </Link>

        {/* Mobile: hamburger menu */}
        <MobileMenuSheet>
          {({ close }) => (
            <>
              <div className="flex flex-col gap-1">
                {isAuthenticated ? (
                  <>
                    <MobileMenuItem to="/ax-news" icon={icRocket} label="AX News" onClick={close} />
                    <MobileMenuItem
                      to="/my-page"
                      icon={icUserCircle}
                      label="My page"
                      onClick={close}
                    />
                    <div className="my-1 h-px bg-black/[0.09]" />
                    <p className="px-2 py-2 text-body-sm text-[#8D8D8D]">AI AGENT</p>
                    <MobileMenuItem
                      to="/chat"
                      icon={icMessagePlusSquare}
                      label="New chat"
                      onClick={close}
                    />
                    <MobileMenuItem
                      to="/projects"
                      icon={icArchive}
                      label="Project list"
                      onClick={close}
                    />
                    <MobileMenuItem
                      to="/agents"
                      icon={icCpuChip}
                      label="Agent list"
                      onClick={close}
                    />
                  </>
                ) : (
                  <MobileMenuItem to="/ax-news" icon={icRocket} label="AX News" onClick={close} />
                )}
              </div>

              {!isAuthenticated && (
                <div className="flex flex-col gap-3 ">
                  <Link
                    to="/signup"
                    onClick={close}
                    className="flex items-center justify-center w-full h-[48px] bg-[#EBEBEB] text-black text-base font-bold rounded-[4px]"
                  >
                    Sign up
                  </Link>
                  <Link
                    to="/login"
                    onClick={close}
                    className="flex items-center justify-center w-full h-[48px] bg-[#FFAA72] text-black text-base font-bold rounded-[4px]"
                  >
                    Log in
                  </Link>
                </div>
              )}
            </>
          )}
        </MobileMenuSheet>

        {/* Desktop: full nav */}
        <div className={`hidden lg:flex items-center ${isAuthenticated ? "gap-12" : "gap-4"}`}>
          {isLoading ? null : isAuthenticated ? (
            <>
              <Link to="/ax-news" className="text-base font-bold text-black transition-colors">
                AX News
              </Link>
              <Link to="/my-page" className="text-base font-bold text-black transition-colors">
                My page
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-12 h-12 rounded-full"
                    aria-label="Menu"
                  >
                    <img src={icMenu} alt="" width={48} height={48} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px] rounded-[4px]">
                  <DropdownMenuItem asChild className="cursor-pointer p-2">
                    <Link
                      to="/chat"
                      className="flex items-center gap-2 hover:bg-[#EBEBEB] rounded-[4px] transition-colors"
                    >
                      <img src={icMessagePlusSquare} alt="" width={24} height={24} />
                      <span className="text-base font-medium text-black">New chat</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer p-2">
                    <Link
                      to="/projects"
                      className="flex items-center gap-2 hover:bg-[#EBEBEB] rounded-[4px] transition-colors"
                    >
                      <img src={icArchive} alt="" width={24} height={24} />
                      <span className="text-base font-medium text-black">Project list</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer p-2">
                    <Link
                      to="/agents"
                      className="flex items-center gap-2 hover:bg-[#EBEBEB] rounded-[4px] transition-colors"
                    >
                      <img src={icCpuChip} alt="" width={24} height={24} />
                      <span className="text-base font-medium text-black">Agent list</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/ax-news" className="text-base font-bold text-black transition-colors">
                AX News
              </Link>
              <Button
                variant="ghost"
                asChild
                className="w-[140px] h-[48px] text-base font-bold bg-[#F3F3F1] backdrop-blur-[40px] hover:bg-[#F3F3F1] text-black rounded-[4px]"
              >
                <Link to="/login">Log in</Link>
              </Button>
              <Button
                asChild
                className="w-[140px] h-[48px] text-base font-bold rounded-[4px] bg-[#FFAA72] text-black"
              >
                <Link to="/signup">시작하기</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

function MobileMenuItem({
  to,
  icon,
  label,
  onClick,
}: {
  to: string;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-2 py-2 rounded hover:bg-black/[0.04] `}
    >
      <img src={icon} alt="" width={24} height={24} />
      <span className="text-base font-medium text-black">{label}</span>
    </Link>
  );
}
